import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { BuildingEntityBase } from "building-types/entities/buildingEntityBase";
import type { Framing } from "building-types/framing/framingTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { RoomClass } from "building-types/rooms/roomTypesEnum";
import type { ColumnCatalogSettings } from "building-types/settings/columnCatalogSettings";
import type { FoundationSettings } from "building-types/settings/foundationSettings";
import type { PorchSettingsBase } from "building-types/settings/porchSettingsTypes";
import type { WallSettings } from "building-types/settings/wallSettings";
import type { PathPriority } from "building-types/walls/pathPriority";
import type { WallSideRoomDescriptor } from "building-types/walls/wallDesciptorTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import type { Extrusion, Face } from "geom/brep";
import type { PlanarGraphLoops } from "geom/planarGraph";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { FloorAssembly, FloorAssemblyHashParameters } from "./floorAssemblyTypes";
import type { StructuralFloorType } from "./floorComponentsTypes";
import type { SheathingType } from "./sheathingComponentTypes";

export { JoistPlacement } from "./floorComponentsTypes";

/**
 * @NOTE - FloorPath and FoundationPath should be deprecated in the future in favor of WallPaths. Most of this information is
 * duplicated in WallPaths, and we no longer need to maintain 3 different path types. This means creating an early dedicated
 * generation stage for WallPaths that would contain all necessary information about Walls/Floors/Foundations.
 *
 * We should also consider renaming WallPath to HalfEdgePath
 */
export interface FloorPath {
  readonly halfEdgeIndex: number;
  readonly planarGraphId: string; // planarGraphId - levelId - used to look up the planar graph
  readonly leftWallRoom: WallSideRoomDescriptor;
  readonly rightWallRoom: WallSideRoomDescriptor;
  readonly leftRoom: Room; // TODO - deprecate and use wallRoom
  readonly rightRoom: Room; // TODO - deprecate and use wallRoom
  readonly leftRoomClass: RoomClass; // TODO - deprecate and use wallRoom
  readonly rightRoomClass: RoomClass; // TODO - deprecate and use wallRoom
  readonly leftWallOffset: number;
  readonly rightWallOffset: number;
  readonly leftFloorAssembly: FloorAssembly;
  readonly rightFloorAssembly: FloorAssembly;
  readonly floorOffset: number;
  readonly floorPriority: PathPriority;
  readonly doors: GeneratedDoorPlacement[];
  readonly windows: GeneratedWindowPlacement[];
}

export interface FloorPathOffsetSettings {
  readonly wallSettings: WallSettings;
  readonly porchSettings: PorchSettingsBase;
  readonly rearPorchSettings: PorchSettingsBase | undefined;
  readonly columnSettings: ColumnCatalogSettings;
  readonly foundationSettings: FoundationSettings;
}

export interface FloorPlate {
  readonly perimeters: PlanarGraphLoops;
  readonly perimeterPolyLine2s: PolyLine2[];
  readonly perimeterFaces: Face[];
  readonly framingPerimeters: PlanarGraphLoops;
  readonly framingPerimeterPolyLine2s: PolyLine2[];
  readonly framingPerimeterFaces: Face[];
  // readonly holes: PlanarGraphLoops;
  // readonly holeIndex: number[][];
}

export interface SolidFloorBase<T extends StructuralFloorType> extends FloorBase<FloorType.SOLID> {
  readonly floorElevation: WorldInches;
  readonly floorPlate: FloorPlate;
  readonly floorPlateThickness: number;
  readonly subFloor: Extrusion[];
  readonly subfloorName?: string;
  readonly subfloorType?: SheathingType;
  readonly subfloorThickness?: number;
  readonly subfloorComponentId?: string;
  readonly crawlSpace: boolean;
  readonly structuralType: T;
  /** The hash used to group the floor assemblies to create this floor. */
  readonly hashParams: Partial<FloorAssemblyHashParameters>;
}

export interface SolidFramingFloor
  extends SolidFloorBase<StructuralFloorType.FRAMING>,
    BuildingEntityBase<"FRAMED_FLOOR_ASSEMBLY"> {
  readonly floorFraming: Framing[];
}

export interface SolidSlabFloor
  extends SolidFloorBase<StructuralFloorType.SLAB>,
    BuildingEntityBase<"SLAB_FLOOR_ASSEMBLY"> {
  readonly slab: Extrusion[];
}

export type SolidFloor = SolidFramingFloor | SolidSlabFloor;

export type Floor = SolidFloor | VoidFloor;

export enum FloorType {
  SOLID = "SOLID",
  NONE = "NONE",
}

interface FloorBase<T extends FloorType> {
  readonly roomClass: RoomClass;
  readonly floorType: T;
}

export type VoidFloor = FloorBase<FloorType.NONE>;

export enum PlateType {
  SILL = "SILL",
}
